import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '@/lib/capacitor';

interface LocalUser {
  id: string;
  name: string;
  uuid: string;
}

interface GoogleUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

type User = (LocalUser & { authType: 'local' }) | (GoogleUser & { authType: 'google' });

const LOCAL_STORAGE_KEY = 'ping_local_user';
const ACCESS_TOKEN_KEY = 'ping_access_token';
const REFRESH_TOKEN_KEY = 'ping_refresh_token';
const GOOGLE_USER_KEY = 'ping_user';

/**
 * Decode JWT and check if it's expired.
 * Adds a 10-second buffer to account for clock skew.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Add 10 second buffer to account for clock skew
    return payload.exp * 1000 < Date.now() - 10000;
  } catch {
    return true;
  }
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns new tokens on success, null on failure.
 */
async function refreshAccessToken(refreshToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
} | null> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  } catch {
    return null;
  }
}

/**
 * Clear all auth-related data from localStorage.
 */
function clearAuthData(): void {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(GOOGLE_USER_KEY);
}

/**
 * Authenticated fetch wrapper that automatically handles token refresh on 401.
 * - Makes the request with the current access token
 * - If 401 is returned, attempts to refresh the token
 * - If refresh succeeds, retries the original request with new token
 * - If refresh fails, clears auth data and throws an error
 */
async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Get current access token
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  // Make request with current token
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  // If not 401, return response as-is
  if (response.status !== 401) {
    return response;
  }

  // Try to refresh the token
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    clearAuthData();
    throw new Error('Session expired. Please sign in again.');
  }

  const newTokens = await refreshAccessToken(refreshToken);
  if (!newTokens) {
    clearAuthData();
    throw new Error('Session expired. Please sign in again.');
  }

  // Store new tokens
  localStorage.setItem(ACCESS_TOKEN_KEY, newTokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, newTokens.refreshToken);

  // Retry the original request with new token
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${newTokens.accessToken}`,
    },
  });
}

// Detect if we're running on the public server
// Uses build-time VITE_SERVER_TYPE when available, otherwise falls back to runtime detection
export function isPublicServer(): boolean {
  // Check build-time embedded server type first (most reliable for production)
  const buildTimeServerType = import.meta.env.VITE_SERVER_TYPE;
  if (buildTimeServerType === 'public') {
    return true;
  }
  if (buildTimeServerType === 'internal') {
    return false;
  }
  
  // Runtime detection for development
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Check for production public domain
  if (hostname === 'ping.nocreativity.com') {
    return true;
  }
  
  // Check for public server ports (dev: 3002, prod: 3902)
  if (port === '3002' || port === '3902') {
    return true;
  }
  
  // Check for internal server indicators (dev: 3001, prod: 3901)
  if (port === '3001' || port === '3901' || hostname === 'ping.nc') {
    return false;
  }
  
  // On port 3000 (Vite dev), check URL params or local storage hint
  // If ?public=true is in URL, treat as public server
  if (window.location.search.includes('public=true')) {
    return true;
  }
  
  // Default to internal for local development
  return false;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresGoogleAuth, setRequiresGoogleAuth] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const isPublic = isPublicServer();
      setRequiresGoogleAuth(isPublic);

      // Always check for Google auth first (needed for admin access on internal server)
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const storedGoogleUser = localStorage.getItem(GOOGLE_USER_KEY);

      if (storedGoogleUser) {
        let parsedUser: GoogleUser;
        try {
          parsedUser = JSON.parse(storedGoogleUser);
        } catch {
          // Invalid stored user, clear Google auth data
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(GOOGLE_USER_KEY);
          // Fall through to check local auth
          parsedUser = null as unknown as GoogleUser;
        }

        if (parsedUser) {
          // Check if we have a valid access token
          if (accessToken && !isTokenExpired(accessToken)) {
            // Token is valid, set user
            setUser({ ...parsedUser, authType: 'google' });
            setIsLoading(false);
            return;
          }

          // Access token missing or expired, try to refresh
          if (refreshToken && !isTokenExpired(refreshToken)) {
            const newTokens = await refreshAccessToken(refreshToken);
            if (newTokens) {
              // Refresh successful, store new tokens and set user
              localStorage.setItem(ACCESS_TOKEN_KEY, newTokens.accessToken);
              localStorage.setItem(REFRESH_TOKEN_KEY, newTokens.refreshToken);
              setUser({ ...parsedUser, authType: 'google' });
              setIsLoading(false);
              return;
            }
          }

          // Refresh failed or no valid refresh token, clear Google auth data
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(GOOGLE_USER_KEY);
        }
      }

      // If not on public server, also check for local auth
      if (!isPublic) {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setUser({ ...parsed, authType: 'local' });
          } catch {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
          }
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Local auth login
  const loginLocal = useCallback(async (name: string): Promise<void> => {
    const response = await fetch(`${getApiBaseUrl()}/api/local-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    const userData = { ...data.user, authType: 'local' as const };
    
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data.user));
    setUser(userData);
  }, []);

  // Get auth headers for API requests
  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (user?.authType === 'google') {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
    }
    return {};
  }, [user]);

  // Get user identifier for uploads
  const getUserIdentifier = useCallback((): { userId?: string; userUuid?: string } => {
    if (!user) return {};
    
    if (user.authType === 'google') {
      return { userId: user.id };
    } else {
      return { userUuid: user.uuid };
    }
  }, [user]);

  const logout = useCallback(() => {
    clearAuthData();
    setUser(null);
  }, []);

  return {
    user,
    isLoading,
    requiresGoogleAuth,
    loginLocal,
    getAuthHeaders,
    getUserIdentifier,
    authenticatedFetch,
    logout,
  };
}

